import { Input, Pagination, Switch } from 'antd';
import { useEffect, useState } from 'react';

import { apiFetch } from '@/api/client';
import { type VendorCapability } from '@/api/generated/enums';
import { useVendors, vendorsKey, type Vendor } from '@/api/masters';
import { useApiMutation } from '@/api/mutations';
import { ApiErrorAlert } from '@/components/ApiErrorAlert';
import { ConfirmButton } from '@/components/ConfirmButton';
import { DataView, type DataViewColumn } from '@/components/DataView';
import { IconAction } from '@/components/IconAction';
import { Button, Card, EmptyState, PageHeading } from '@/components/PageHeading';
import { StatusBadge } from '@/components/StatusBadge';
import { useDebounced } from '@/components/useDebounced';
import { RecordAction, WriteAction } from '@/components/WriteAction';
import { absent } from '@core/format/dates';
import type { Session } from '@core/session/types';

import { capabilityFilters, capabilityLabel } from './vendors/capabilities';
import { VendorDrawer, type Editing } from './vendors/VendorDrawer';

const pageSize = 25;

/**
 * The vendor master (spec Sections 10, 11 and 16).
 *
 * <p>
 * Built ahead of the modules that consume it, because Section 10's cleaning jobs cannot dispatch to
 * a party that has no row — and cleaning is what stands between the shop and a rental loop that
 * closes.
 * </p>
 *
 * <p>
 * <strong>One list, filtered by capability, rather than three screens.</strong> The spec names
 * three tables — laundry vendors, tailors, suppliers — and in a real lane they are frequently the
 * same three people. The chips at the top are what a dispatch screen will ask the API for, so what
 * an operator sees here is what Section 10 will see when it comes to pick one.
 * </p>
 */
export function VendorsPage({ session }: { session: Session }) {
  const [search, setSearch] = useState('');
  const [capability, setCapability] = useState<VendorCapability | undefined>(undefined);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<Editing | null>(null);

  const debouncedSearch = useDebounced(search);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, capability, includeInactive]);

  const vendors = useVendors(session.branch?.id, {
    search: debouncedSearch,
    capability,
    includeInactive,
    page,
    pageSize,
  });

  const remove = useApiMutation<void, Vendor>({
    mutationFn: (vendor) => apiFetch<void>(`/api/vendor/${vendor.id}`, { method: 'DELETE' }),
    success: (_result, vendor) => `${vendor.name} deleted.`,
    invalidates: [vendorsKey],
  });

  const columns: DataViewColumn<Vendor>[] = [
    {
      title: 'Vendor',
      dataIndex: 'name',
      primary: true,
      render: (_, row) => (
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-2">
            <span className="font-medium text-gray-900">{row.name}</span>
            {/* Only the exceptions are marked, as on every other master: shared is the ordinary
                case for a vendor, and branch-local is what an operator needs to spot. */}
            {!row.isShared && <StatusBadge tone="brand">This branch</StatusBadge>}
            {!row.isActive && <StatusBadge tone="warning">Not used</StatusBadge>}
            {row.isMsme && <StatusBadge tone="neutral">MSME</StatusBadge>}
          </div>
          <span className="font-mono-nums text-[12px] text-default">
            {row.code} · {row.mobile}
            {row.contactPerson ? ` · ${row.contactPerson}` : ''}
          </span>
        </div>
      ),
    },
    {
      title: 'Does',
      key: 'capabilities',
      width: 210,
      render: (_, row) => (
        <div className="flex flex-wrap gap-1">
          {row.capabilities.map((capabilityValue) => (
            <StatusBadge key={capabilityValue} tone="neutral">
              {capabilityLabel(capabilityValue)}
            </StatusBadge>
          ))}
        </div>
      ),
    },
    {
      title: 'Turnaround',
      dataIndex: 'standardTurnaroundDays',
      width: 120,
      align: 'right',
      responsive: ['lg'],
      render: (_, row) =>
        row.standardTurnaroundDays === null ? (
          <span className="text-default">{absent}</span>
        ) : (
          <span className="font-mono-nums">
            {row.standardTurnaroundDays === 0 ? 'Same day' : `${row.standardTurnaroundDays} d`}
          </span>
        ),
    },
    {
      title: 'Terms',
      dataIndex: 'paymentTermDays',
      width: 110,
      align: 'right',
      responsive: ['xl'],
      hideOnCard: true,
      render: (_, row) =>
        row.paymentTermDays === null ? (
          <span className="text-default">{absent}</span>
        ) : (
          <span className="font-mono-nums">{row.paymentTermDays} d</span>
        ),
    },
    {
      title: '',
      key: 'actions',
      width: 96,
      align: 'right',
      fixed: 'right',
      trailing: true,
      render: (_, row) => (
        <div className="flex justify-end gap-1">
          <WriteAction session={session} required="Vendor:Edit">
            {(disabled) => (
              <IconAction
                icon="icon icon-pencil-line"
                label={`Edit ${row.name}`}
                disabled={disabled}
                onClick={() => setEditing({ mode: 'edit', vendor: row })}
              />
            )}
          </WriteAction>

          <RecordAction session={session} required="Vendor:Delete">
            {(disabled) => (
              <ConfirmButton
                icon="icon icon-trash-2"
                label={`Delete ${row.name}`}
                title="Delete this vendor?"
                description="Only for one entered by mistake. If you have simply stopped using them, edit them and switch off 'Still used' instead — that keeps the jobs and bills that name them readable."
                confirmText="Delete"
                disabled={disabled}
                pending={remove.isPending}
                onConfirm={() => void remove.mutateAsync(row).catch(() => undefined)}
              />
            )}
          </RecordAction>
        </div>
      ),
    },
  ];

  if (vendors.isError) {
    return <ApiErrorAlert error={vendors.error} />;
  }

  const total = vendors.data?.total ?? 0;

  return (
    <>
      <PageHeading
        title="Vendors"
        subtitle="Who cleans, alters and supplies. One party can do all three."
        actions={
          <WriteAction session={session} required="Vendor:Create">
            {(disabled) => (
              <Button
                variant="primary"
                icon="icon icon-plus"
                shortcut="create"
                disabled={disabled}
                onClick={() => setEditing({ mode: 'create' })}
              >
                New vendor
              </Button>
            )}
          </WriteAction>
        }
      />

      <Card flush className="p-4 pb-0 max-sm:p-3 max-sm:pb-1">
        <div className="mb-4 flex flex-wrap items-center gap-3 max-sm:mb-3 max-sm:gap-2">
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search name, code or number"
            allowClear
            prefix={<i className="icon icon-search text-default" />}
            className="max-w-72 max-sm:max-w-none max-sm:flex-[1_0_100%]"
          />

          {/* The same question Section 10's dispatch screen will ask the API. A vendor who both
              cleans and alters appears under both chips, which is the point of the single master. */}
          <div className="flex flex-wrap gap-1">
            {capabilityFilters.map((filter) => (
              <button
                key={filter.label}
                type="button"
                onClick={() => setCapability(filter.value)}
                aria-pressed={capability === filter.value}
                className={`rounded border px-2.5 py-1 text-[13px] ${
                  capability === filter.value
                    ? 'border-primary bg-primary text-white'
                    : 'border-border-color text-default hover:text-dark'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>

          <label className="inline-flex cursor-pointer items-center gap-2 text-[13px] text-default sm:ms-auto">
            <Switch size="small" checked={includeInactive} onChange={setIncludeInactive} />
            Show unused
          </label>
        </div>

        <DataView<Vendor>
          columns={columns}
          dataSource={vendors.data?.items ?? []}
          loading={vendors.isLoading}
          empty={
            <EmptyState
              icon="icon icon-truck"
              title={debouncedSearch || capability ? 'Nobody matches that' : 'No vendors yet'}
              hint={
                debouncedSearch || capability
                  ? 'Try part of the number instead — the search matches it with or without spaces.'
                  : 'Add the laundry you send garments to. Cleaning jobs will dispatch to whoever is listed here.'
              }
            />
          }
        />

        {total > pageSize && (
          <div className="flex justify-end border-t border-border-color py-3">
            <Pagination
              current={page}
              total={total}
              pageSize={pageSize}
              onChange={setPage}
              showSizeChanger={false}
              showTotal={(count) => `${count} vendors`}
            />
          </div>
        )}
      </Card>

      {editing && <VendorDrawer editing={editing} onClose={() => setEditing(null)} />}
    </>
  );
}
